import { useState } from "react";
import { usePatientInfoTemplates, usePatients, type PatientInfoTemplate, type Patient } from "@/lib/queries";
import {
  useCreatePatientInfoTemplate, useUpdatePatientInfoTemplate, useDeletePatientInfoTemplate,
} from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash, Printer, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { renderMarkdown } from "@emr/shared/markdown";
import { createPatientInfoPacketPdf } from "@/lib/pdf/patientInfoPdf";
import { printPdf } from "@/lib/print";
import { toast } from "sonner";

function TemplateFormDialog({ open, onOpenChange, template, create, update, id }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  template?: PatientInfoTemplate | null;
  create: ReturnType<typeof useCreatePatientInfoTemplate>;
  update: ReturnType<typeof useUpdatePatientInfoTemplate>;
  id?: string;
}) {
  const [form, setForm] = useState({ title: template?.title ?? "", content: template?.content ?? "" });
  const submit = async () => {
    try {
      if (template && id) await update.mutateAsync(form);
      else await create.mutateAsync(form);
      onOpenChange(false);
    } catch { /* handled */ }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{template ? "Edit template" : "Add template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div>
            <Label>Content (Markdown)</Label>
            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} placeholder="## Section heading&#10;&#10;Patient instructions here. **bold**, - bullet lists, 1. numbered lists." />
          </div>
          {form.content && (
            <div className="rounded border border-cream-200 bg-cream-50 p-3">
              <p className="mb-1 text-xs font-medium text-muted">Preview:</p>
              <div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: renderMarkdown(form.content) }} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!form.title || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrintPacketDialog({ open, onOpenChange, templates }: { open: boolean; onOpenChange: (v: boolean) => void; templates: PatientInfoTemplate[] }) {
  const [patientQuery, setPatientQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const { data: patientsData } = usePatients(patientQuery, 1, 10);
  const patientOptions = patientsData?.rows ?? [];
  const [busy, setBusy] = useState(false);

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds((s) => s.filter((x) => x !== id));
      setOrder((o) => o.filter((x) => x !== id));
    } else {
      setSelectedIds((s) => [...s, id]);
      setOrder((o) => [...o, id]);
    }
  };

  const move = (id: string, dir: "up" | "down") => {
    const idx = order.indexOf(id);
    if (idx < 0) return;
    const newIdx = dir === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= order.length) return;
    const next = [...order];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setOrder(next);
  };

  const print = async () => {
    if (!selectedPatient) { toast.error("Select a patient first."); return; }
    if (order.length === 0) { toast.error("Select at least one template."); return; }
    setBusy(true);
    try {
      const ordered = order.map((id) => templates.find((t) => t.id === id)).filter(Boolean) as PatientInfoTemplate[];
      const doc = await createPatientInfoPacketPdf({
        templates: ordered,
        patientName: [selectedPatient.salutation, selectedPatient.name].filter(Boolean).join(" "),
        clinicUhid: selectedPatient.clinicUhid,
        patientDob: selectedPatient.dob,
        patientGender: selectedPatient.gender,
      });
      printPdf(doc);
      onOpenChange(false);
    } catch (e) {
      toast.error("PDF generation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Print Patient Info Packet</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Patient *</Label>
            <Input value={patientQuery} onChange={(e) => setPatientQuery(e.target.value)} placeholder="Type name/phone/UHID to search…" />
            {patientOptions.length > 0 && (
              <div className="mt-1 max-h-32 overflow-auto rounded border border-cream-200 bg-cream-100">
                {patientOptions.map((p) => (
                  <button key={p.id} type="button" className="block w-full px-3 py-1 text-left text-sm hover:bg-cream-200" onClick={() => { setSelectedPatient(p); setPatientQuery([p.salutation, p.name].filter(Boolean).join(" ")); }}>
                    {[p.salutation, p.name].filter(Boolean).join(" ")} · {p.clinicUhid} · {p.phone}
                  </button>
                ))}
              </div>
            )}
            {selectedPatient && <p className="mt-1 text-xs text-green-700">Selected: {selectedPatient.name}</p>}
          </div>
          <div>
            <Label>Templates (select + reorder)</Label>
            <div className="max-h-64 space-y-1 overflow-auto rounded border border-cream-200 p-2">
              {templates.map((t) => {
                const isSelected = selectedIds.includes(t.id);
                const idx = order.indexOf(t.id);
                return (
                  <div key={t.id} className={`flex items-center gap-2 rounded p-1 ${isSelected ? "bg-cream-100" : ""}`}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggle(t.id)} />
                    <GripVertical size={12} className="text-muted" />
                    <span className="flex-1 text-sm">{t.title}</span>
                    {isSelected && (
                      <>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => move(t.id, "up")} disabled={idx === 0}><ArrowUp size={12} /></Button>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => move(t.id, "down")} disabled={idx === order.length - 1}><ArrowDown size={12} /></Button>
                        <Badge variant="outline" className="text-xs">{idx + 1}</Badge>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={print} disabled={busy || !selectedPatient || order.length === 0} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"><Printer size={14} className="mr-1" /> Print Packet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PatientInfoPage() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("patientinfo.create");
  const canEdit = isAdmin || permissions.has("patientinfo.edit");
  const canDelete = isAdmin || permissions.has("patientinfo.delete");
  const { data, isLoading } = usePatientInfoTemplates();
  const [addOpen, setAddOpen] = useState(false);
  const [editTpl, setEditTpl] = useState<PatientInfoTemplate | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const create = useCreatePatientInfoTemplate();
  const update = useUpdatePatientInfoTemplate("");
  const del = useDeletePatientInfoTemplate();
  const rows = data?.rows ?? [];

  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Patient Info Sheets</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setPrintOpen(true)} disabled={rows.length === 0}><Printer size={16} className="mr-1" /> Print Packet</Button>
          {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={() => { setEditTpl(null); setAddOpen(true); }}><Plus size={16} className="mr-1" /> Add Template</Button>}
        </div>
      </div>

      {isLoading ? <Skeleton className="h-40" /> :
       rows.length === 0 ? <Card><CardContent className="py-8 text-center text-sm text-muted">No patient info templates yet.</CardContent></Card> :
       <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {rows.map((t: PatientInfoTemplate) => (
          <Card key={t.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="font-serif text-base text-maroon-700 dark:text-amber-500">{t.title}</CardTitle>
              <div className="flex gap-1">
                {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditTpl(t); setAddOpen(true); }}><Pencil size={14} /></Button>}
                {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del.mutate(t.id)}><Trash size={14} /></Button>}
              </div>
            </CardHeader>
            <CardContent>
              {t.content ? (
                <div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: renderMarkdown(t.content) }} />
              ) : (
                <p className="text-sm text-muted">No content</p>
              )}
            </CardContent>
          </Card>
        ))}
       </div>
      }

      <TemplateFormDialog open={addOpen} onOpenChange={setAddOpen} template={editTpl} create={create} update={useUpdatePatientInfoTemplate(editTpl?.id ?? "")} id={editTpl?.id} />
      <PrintPacketDialog open={printOpen} onOpenChange={setPrintOpen} templates={rows} />
    </div>
  );
}
